import SuperFetch from 'library/helpers/superFetch';
import { removeEmptyOrNull } from 'library/helpers/utility';
import { call, put, select, takeLatest } from 'redux-saga/effects';

/**
 * Shared entity "Activity" feed SAGA — fetches the access-log feed for an entity
 * drawer's Activity tab. This module imports SuperFetch (→ store), so it must be
 * imported ONLY by sagas, never by slices (that would create a slice → store →
 * slice cycle). Slices import the pure reducers from ./activityState instead.
 *
 * Backend scope (the /logs/accessLogs endpoint ANDs whatever params are sent):
 *   - device → deviceIDs (this lock only)      - team  → personIDs (all members)
 *   - space  → floorID (the server resolves    - group → deviceIDs (group doors)
 *              the floor plan's devices)
 *   - site   → siteID (native site scope)      - person→ personIDs
 *   - credential → credentialIDs (every use of this card or passcode)
 */

/**
 * Builds the saga worker that fetches an entity's activity feed.
 *
 * @param {object} actions - the slice's action creators (needs loadActivity* )
 * @returns a worker generator `function*(action)` for takeLatest(loadActivity, …)
 */
export function makeFetchActivity(actions) {
	return function* fetchActivity(action) {
		const { scopeKey, params } = action.payload || {};
		try {
			// SuperFetch pins the orgID into the URL path; pass it explicitly so the
			// request never falls back to an undefined path segment (→ 404).
			const orgID = yield select((state) => state.Auth?.selectedProfile?.orgID);
			if (!orgID) {
				yield put(actions.loadActivityError({ scopeKey, error: 'No organisation selected.' }));
				return;
			}

			const query = removeEmptyOrNull({ pageSize: 50, page: 1, ...params });
			const res = yield call(SuperFetch.get, '/logs/accessLogs', {}, query, orgID);

			if (res.statusCode === 200) {
				const logs = res.data || [];
				yield put(
					actions.loadActivitySuccess({
						scopeKey,
						logs,
						total: Number(res.pagination?.total) || logs.length
					})
				);
			} else {
				throw new Error(res.message || 'Failed to load activity.');
			}
		} catch (error) {
			yield put(actions.loadActivityError({ scopeKey, error: error.message || String(error) }));
		}
	};
}

/**
 * Convenience watcher for a slice's root saga:
 *   yield* watchActivity(devicesActions)
 */
export function* watchActivity(actions) {
	yield takeLatest(actions.loadActivity, makeFetchActivity(actions));
}

/**
 * Re-fetch whatever scope this slice is currently holding, if any.
 *
 * For use after a mutation that PRODUCES activity — a remote unlock/lock writes a
 * `Log.ExternalAccessLog` row carrying the client that sent it, so the operator should see their own
 * command appear in the open drawer's Activity tab instead of a feed that silently went stale.
 *
 * A no-op when no scope is held (no drawer open, nothing to refresh), so callers can fire it blindly.
 * `params` come from the state the trigger stored, never from re-parsing `scopeKey`.
 *
 * @param {object} actions - the slice's action creators (needs loadActivity)
 * @param {string} storeKey - the slice's key in the root state, e.g. 'Devices'
 */
export function* refreshHeldActivity(actions, storeKey) {
	const held = yield select((state) => state[storeKey]?.activity);
	if (!held?.scopeKey || !held?.params) return;
	yield put(actions.loadActivity({ scopeKey: held.scopeKey, params: held.params }));
}
